import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LineChart, ArrowRight, BadgeCheck, Sparkles, Trophy, GraduationCap,
  Zap, MessageSquare, Wallet, TrendingUp, ChevronRight
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const niches = [
  { tag: "AFF", label: "Affiliate" },
  { tag: "DRP", label: "Dropshipping" },
  { tag: "FRL", label: "Freelance" },
  { tag: "CRY", label: "Crypto" },
  { tag: "CNT", label: "Content" },
  { tag: "HLT", label: "Health & Fitness" },
  { tag: "AI",  label: "AI" },
];

const features = [
  { icon: BadgeCheck, title: "Verified Income Proofs", desc: "Real screenshots. Real numbers. No gurus selling courses about courses." },
  { icon: Sparkles, title: "AI Money Coach", desc: "Tell it your situation. Get a step-by-step playbook tuned to your niche." },
  { icon: GraduationCap, title: "Bite-size Lessons", desc: "5-minute lessons from people actually making money. Skip the fluff." },
  { icon: Trophy, title: "Live Leaderboards", desc: "See who's earning what — this week, this month, all-time. Compete with the room." },
  { icon: Zap, title: "Money Challenges", desc: "Join $0→$1k in 30 days. Public accountability. Daily check-ins." },
  { icon: MessageSquare, title: "Niche Mentors", desc: "DM people who've already done it. No more cold-emailing influencers." },
];

export default function Landing() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_reported_earnings: 0, users_count: 0, total_proofs: 0, lessons_count: 0 });

  useEffect(() => {
    api.get("/stats/global").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-[#F2F2F2] overflow-hidden">
      <div className="fixed inset-0 grid-bg pointer-events-none" />

      {/* Top ticker */}
      <div className="relative z-10 border-b border-white/5 bg-[#0A0A0A] overflow-hidden">
        <div className="ticker-track flex items-center gap-8 py-2.5 text-xs font-mono">
          {Array.from({ length: 2 }).flatMap((_, i) =>
            niches.map((n, j) => (
              <span key={`${i}-${j}`} className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-white/40">{n.tag}</span>
                <span className="text-[#00E676]">{n.label}</span>
                <TrendingUp size={11} className="text-[#00E676]" />
                <span className="text-white/30">+{Math.floor(Math.random() * 80 + 10)}%</span>
                <span className="text-white/15">|</span>
              </span>
            ))
          )}
        </div>
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-[#00E676] flex items-center justify-center">
            <LineChart size={20} strokeWidth={2.5} className="text-black" />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">Cashflow</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-white/55">
          <a href="#features" className="hover:text-white transition">Features</a>
          <a href="#niches" className="hover:text-white transition">Niches</a>
          <a href="#proof" className="hover:text-white transition">Live Proof</a>
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <button
              data-testid="header-cta-feed"
              onClick={() => navigate("/feed")}
              className="cta-green rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
            >
              Enter <ArrowRight size={14} strokeWidth={2.5} />
            </button>
          ) : (
            <>
              <button
                data-testid="header-cta-signin"
                onClick={login}
                className="text-sm text-white/70 hover:text-white px-3 py-2 transition"
              >
                Sign in
              </button>
              <button
                data-testid="header-cta-signup"
                onClick={login}
                className="cta-green rounded-md px-4 py-2 text-sm font-semibold"
              >
                Get started
              </button>
            </>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24">
        {/* HERO */}
        <section className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center mb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 surface rounded-full px-3 py-1.5 mb-6 text-xs font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] pulse-dot" />
              <span className="text-white/65">{stats.users_count.toLocaleString()} earners online</span>
            </div>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6">
              Make money online.<br />
              <span className="text-[#00E676]">Together.</span>
            </h1>
            <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">
              Cashflow is the social network for people learning to earn online. Verified income proofs, AI money coaches,
              bite-size lessons, and 7 niches of real operators sharing exactly what's working <em>this week</em>.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-12">
              <button
                data-testid="hero-cta-signin"
                onClick={user ? () => navigate("/feed") : login}
                className="cta-green rounded-md px-6 py-3.5 font-semibold flex items-center gap-2"
              >
                {user ? "Open dashboard" : "Start earning"} <ArrowRight size={16} strokeWidth={2.5} />
              </button>
              <a
                href="#features"
                data-testid="hero-cta-tour"
                className="surface rounded-md px-6 py-3.5 text-sm font-medium hover:bg-white/[0.04] transition"
              >
                See how it works
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Reported earned" value={`$${(stats.total_reported_earnings || 0).toLocaleString()}`} />
              <Stat label="Income proofs" value={stats.total_proofs.toLocaleString()} />
              <Stat label="Lessons" value={stats.lessons_count.toLocaleString()} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
            <div className="surface rounded-xl p-5 glow-green">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck size={14} className="text-[#00E676]" fill="#00E676" stroke="#0A0A0A" strokeWidth={2.5} />
                <span className="text-xs font-mono uppercase tracking-wider text-[#00E676]">Verified Income Proof</span>
                <span className="ml-auto text-xs font-mono text-white/30">live · 2m ago</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://images.unsplash.com/photo-1766022411633-e88e3650538b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgxNzE5NzUyfDA&ixlib=rb-4.1.0&q=85" alt="" className="w-11 h-11 rounded-md object-cover" />
                <div>
                  <div className="font-semibold text-sm flex items-center gap-1.5">
                    Maya R. <BadgeCheck size={12} className="text-[#00E676]" fill="#00E676" stroke="#0A0A0A" strokeWidth={2.5} />
                  </div>
                  <div className="text-[11px] text-white/40 font-mono">AFFILIATE · 7-day streak</div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-[#00E676]/8 to-transparent border border-[#00E676]/15 mb-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">Earned last 7 days</div>
                <div className="font-mono text-5xl text-[#00E676] tracking-tighter font-semibold live-num" style={{ textShadow: "0 0 24px rgba(0,230,118,0.35)" }}>
                  $4,820
                </div>
              </div>
              <p className="text-sm text-white/75 leading-relaxed mb-3">
                Switched from broad keywords to long-tail product reviews. Conversion rate jumped 3.4x. Posting the exact funnel inside.
              </p>
              <div className="flex gap-2 text-xs font-mono text-white/40">
                <span>#affiliate</span><span>#seo</span><span>#review</span>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <MiniStat label="Top this week" value="$48,210" sub="DRP · Liam K." />
              <MiniStat label="New lesson" value="Cold DM playbook" sub="FRL · 6 min" />
            </div>
          </motion.div>
        </section>

        {/* NICHES */}
        <section id="niches" className="mb-24">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-white/40 mb-3">Niches</div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-10 max-w-3xl">
            Pick your lane. Or run multiple.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {niches.map((n, i) => (
              <motion.div
                key={n.tag}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="surface rounded-lg p-4 text-center card-hover cursor-pointer"
                data-testid={`niche-${n.tag}`}
              >
                <div className="font-mono text-xs text-white/40 mb-2">{n.tag}</div>
                <div className="font-heading font-bold text-base">{n.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" className="mb-24">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-white/40 mb-3">What's inside</div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-10 max-w-3xl">
            Everything you need to go from <span className="text-white/40">$0</span> → <span className="text-[#00E676]">first dollar</span>.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="surface rounded-lg p-6 card-hover"
                data-testid={`feature-${i}`}
              >
                <div className="w-9 h-9 rounded-md bg-[#00E676]/10 border border-[#00E676]/20 flex items-center justify-center mb-4">
                  <f.icon size={16} className="text-[#00E676]" strokeWidth={2} />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-1.5">{f.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mb-20">
          <div className="surface-elev rounded-2xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="relative">
              <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-3 max-w-2xl mx-auto">
                Stop watching <span className="text-white/40">YouTube tutorials</span>. <br />
                Start <span className="text-[#00E676]">earning</span>.
              </h2>
              <p className="text-white/60 mb-8 max-w-md mx-auto">Free forever. No ads. Just a room full of people building real income.</p>
              <button
                data-testid="footer-cta-signin"
                onClick={user ? () => navigate("/feed") : login}
                className="cta-green rounded-md px-7 py-3.5 font-semibold inline-flex items-center gap-2"
              >
                {user ? "Open Cashflow" : "Continue with Google"} <ArrowRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </section>

        <footer className="text-center py-8 text-xs font-mono text-white/30">
          Cashflow · the make-money-online social network · 2026
        </footer>
      </main>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono text-xl text-[#00E676] tracking-tight font-semibold">{value}</div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">{label}</div>
  </div>
);

const MiniStat = ({ label, value, sub }) => (
  <div className="surface rounded-lg p-3">
    <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1">{label}</div>
    <div className="font-mono text-base text-white truncate">{value}</div>
    <div className="text-[10px] font-mono text-white/40 truncate">{sub}</div>
  </div>
);
